import { Edit03Icon } from '@langchain/untitled-ui-icons';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { AddToAnnotationQueuePopoverContent } from '@/components/AnnotationQueuePopover/AddToAnnotationQueuePopoverContent';
import { AnnotationQueueCrudPane } from '@/components/AnnotationQueuePopover/AnnotationQueueCrudPane';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button } from '@/design-system';
import {
  useCreateAnnotationQueueRunsMutation,
  useOrganizationId,
} from '@/hooks/useSwr';
import useToastOn429Error from '@/hooks/useToastOn429Error';
import { AnnotationQueueSchema } from '@/types/schema';
import {
  apiAnnotationQueuesPath,
  appAnnotationQueuesPath,
  appOrganizationPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import useToast from '../Toast/useToast';

export function AddToAnnotationQueueButton(props: {
  variant: 'actions' | 'toolbar';
  runIds: string[] | string;
  onSuccess?: () => void;
  className?: string;
  trigger?: (onClick: () => void) => React.ReactNode;
  runDisplayText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const runDisplayText = props.runDisplayText ?? 'selected run';

  const organizationId = useOrganizationId();

  const createAnnotationQueueRuns = useCreateAnnotationQueueRunsMutation();
  const { handle429Error } = useToastOn429Error();
  const { createToast } = useToast();

  const popoverContentProps =
    props.variant === 'toolbar'
      ? ({ align: 'start', avoidCollisions: false, side: 'top' } as const)
      : {};

  const buttonProps =
    props.variant === 'toolbar'
      ? ({
          size: 'md',
          color: 'primary',
          'data-testid': 'send-to-annotation-queue-button',
        } as const)
      : ({ size: 'sm', color: 'secondary' } as const);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {props.trigger ? (
          props.trigger(() => setOpen(!open))
        ) : (
          <Button
            {...buttonProps}
            variant="outlined"
            className={cn('grow', props.className)}
            leftDecorator={Edit03Icon}
            loading={createAnnotationQueueRuns.isMutating}
          >
            Add to Annotation Queue
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent {...popoverContentProps} className="w-[508px] p-0">
        <AddToAnnotationQueuePopoverContent
          handleSendToAnnotationQueue={async (annotationQueue) => {
            setOpen(false);
            const json =
              typeof props.runIds === 'string' ? [props.runIds] : props.runIds;
            try {
              await createAnnotationQueueRuns.trigger({
                url: `${apiAnnotationQueuesPath}/${annotationQueue.id}/runs`,
                json,
              });
              props.onSuccess?.();
              createToast({
                error: false,
                title: '',
                description: (
                  <span>
                    <span className="text-md text-white dark:text-black">{`Successfully added ${
                      json.length > 1
                        ? `the ${runDisplayText}s`
                        : `the ${runDisplayText}`
                    } to `}</span>
                    <Link
                      to={`/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${annotationQueue.id}`}
                    >
                      <span className="text-md text-brand-green-200 underline">
                        {annotationQueue.name}
                      </span>
                    </Link>
                  </span>
                ),
              });
            } catch (e) {
              handle429Error(e as Error, 'annotation-queue');
            }
          }}
          openNewAnnotationQueueModal={() => setCreateOpen(true)}
        />
      </PopoverContent>
      <AnnotationQueueCrudPane
        open={createOpen}
        closeModal={() => setCreateOpen(false)}
        onSuccess={async (aq: AnnotationQueueSchema) => {
          const json =
            typeof props.runIds === 'string' ? [props.runIds] : props.runIds;
          try {
            await createAnnotationQueueRuns.trigger({
              url: `${apiAnnotationQueuesPath}/${aq.id}/runs`,
              json,
            });
            props.onSuccess?.();
            createToast({
              error: false,
              title: '',
              description: (
                <span>
                  <span className="text-md text-white dark:text-black">{`Successfully created new annotation queue `}</span>
                  <Link
                    to={`/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${aq.id}`}
                  >
                    <span className="text-md text-brand-green-200 underline dark:text-brand-green-200">
                      {aq.name}
                    </span>
                  </Link>
                  <span className="text-md text-white dark:text-black">{` and added ${
                    json.length
                  } ${
                    json.length > 1 ? `${runDisplayText}s` : runDisplayText
                  }`}</span>
                </span>
              ),
            });
          } catch (e) {
            handle429Error(e as Error, 'annotation-queue');
          }
        }}
      />
    </Popover>
  );
}
